/* ------------------------ Functional imports start ------------------------ */
import QueryKeyFactory from "./utils/queryKeyFactory";
import {
  DefaultError,
  MutationFunctionContext,
  QueryClient,
  useMutation,
  UseMutationOptions,
  UseMutationResult
} from "@tanstack/react-query";
import { getPKfromSelf } from "../../utils/functions";
import { DetailResponse, PaginatedResponse } from "../client";
import LogTool from "../../logger/logTools";
import { availableTablePageSizes } from '../../config/ui'
import { EntityBaseType } from "../types";
import { queryClient } from "../../config/queryClient";
/* ------------------------- Functional imports end ------------------------- */

interface UseSmartMutationOptions<
  TData,
  TError,
  TVariables,
  TOnMutateResult = unknown
> extends UseMutationOptions<TData, TError, TVariables, TOnMutateResult>
{
  queryKeyFactory: QueryKeyFactory
}

interface UseOptimisticMutationOptions<TEntity>
  extends UseMutationOptions<TEntity, Error, TEntity>
{
  queryKeyFactory: QueryKeyFactory

}

interface CacheSnapshot<TEntity> {
  listSnapshots: {
    queryKey: readonly unknown[]
    rollbackValue: PaginatedResponse<TEntity>
  }[],
  detailSnapshots: {
    queryKey: readonly unknown[],
    rollbackValue: DetailResponse<TEntity>
  }[]
}

/* ------------------------ Utility functions start ------------------------- */
/**
 * Returns all currently active queries. These are the queries whose data is currently
 * displayed in the UI.
 *
 * @param queryClient - The QueryClient instance to retrieve the active queries for.
 * @returns An array of active queries.
 */
function getActiveQueries(queryClient: QueryClient) {
  const queryCache = queryClient.getQueryCache()
  return queryCache.findAll({ type: 'active' })
}

/**
 * Returns the query keys for all currently active queries. These are the queries
 * whose data is currently displayed in the UI.
 *
 * @param queryClient - The QueryClient instance to retrieve the active queries for.
 * @returns An array of query keys for all active queries.
 */
function getActiveQueryKeys(queryClient: QueryClient): (readonly unknown[])[] {
  return getActiveQueries(queryClient).map(query => query.queryKey)
}

/**
 *
 * @param toUpdate
 * @param update
 */
function applyArrayUpdate(toUpdate: unknown[], update: unknown[]): void {
  // handle edge cases
  if(toUpdate.every(element => typeof element === 'object')
    && update.every(element => typeof element === 'string'))
  {
    // the array to update contains expanded data not included in
    // the update -> do not overwrite
    return
  }
  // update data
  toUpdate = [...update]
}

/**
 * Applies the properties of an update to an entity without overwriting expanded
 * properties.
 * @param toUpdate
 * @param update
 */
function applyEntityUpdate<T extends object>(toUpdate: T, update: T): void {
  for(const property in update) {
    if(property in toUpdate) {
      if(Array.isArray(toUpdate[property]) && Array.isArray(update[property])) {
        applyArrayUpdate(toUpdate[property], update[property])
      }
      else if(typeof toUpdate[property] === 'object' && typeof update[property] === 'object'
        && toUpdate[property] !== null && update[property] !== null
      ) {
        // we found a nested entity -> recursively apply update
        applyEntityUpdate(toUpdate[property], update[property])
      }
      else if(typeof toUpdate[property] !== typeof update[property]) {
        if((typeof toUpdate[property] === 'object' || Array.isArray(toUpdate[property]))
          && typeof update[property] === 'string') {
          // the entity to update contains expanded data not included in
          // the update -> do not overwrite
          continue
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      toUpdate[property] = update[property]
    }
  }
}
/* ------------------------- Utility functions end -------------------------- */

export function useSmartPatchMutation<
  TData extends EntityBaseType,
  TVariables extends { idOrSelf: number | string, data: Partial<TData>},
  TError = DefaultError,
  TOnMutateResult = unknown
>(
  options: UseSmartMutationOptions<TData, TError, TVariables, TOnMutateResult>,
  queryClient?: QueryClient
) {
  return useMutation({
    ...options,
    onSuccess: async (response, input, onMutateResult, context) => {
      const activeKeys = getActiveQueryKeys(context.client)
      // manually inject update into cache as this saves us one unnecessary backend request
      for(const key of activeKeys) {
        if(key.includes('list')) {
          context.client.setQueryData(
            key,
            (previousPage: PaginatedResponse<TData>): PaginatedResponse<TData> => {
              const updatedResults = [...previousPage.results]
              if(Array.isArray(updatedResults) && updatedResults.length > 0) {
                const index = updatedResults.findIndex(
                  cachedEntity => cachedEntity.self === response.self
                )
                if(index !== -1) applyEntityUpdate(updatedResults[index], response)
              }
              return {...previousPage, results: updatedResults}
            }
          )
        }
        if(key.includes('detail')) {
          context.client.setQueryData(
            key,
            (previousEntry: DetailResponse<TData>) => applyEntityUpdate(previousEntry, response)
          )
        }
      }
      // propagate callback
      options.onSuccess && options.onSuccess(response, input, onMutateResult, context)

      await context.client.invalidateQueries({type: 'inactive'})
    },
    onSettled: async (
      response: TData | undefined,
      error: TError | null,
      input: TVariables,
      onMutateResult: TOnMutateResult | undefined,
      context: MutationFunctionContext
    ) => {
      // The mutation either was successful or failed. Invalidate all affected
      // queries to ensure consistency with the backend.
      await context.client.invalidateQueries({
        queryKey: options.queryKeyFactory.lists()
      })
      await context.client.invalidateQueries({
        queryKey: options.queryKeyFactory.details(Number(getPKfromSelf(input.idOrSelf as string)))
      })

      // propagate callback
      options.onSettled?.(response, error, input, onMutateResult, context)
    }
  }, queryClient)
}

export function useSmartUpdateMutation<
  TData extends EntityBaseType,
  TVariables extends{ idOrSelf: number | string, data: Partial<TData>},
  TError = DefaultError,
  TOnMutateResult = unknown
>(
  options: UseSmartMutationOptions<TData, TError, TVariables, TOnMutateResult>,
  queryClient?: QueryClient
) {
  // an update is just a special case of a patch mutation
  return useSmartPatchMutation(options, queryClient)
}

export function useSmartCreateMutation<
  TData extends EntityBaseType,
  TError = DefaultError,
  TVariables = {data: Partial<TData>},
  TOnMutateResult = unknown
>(
  options: UseSmartMutationOptions<TData, TError, TVariables, TOnMutateResult>
) {
  return useMutation({
    ...options,
    onSuccess: (response, input, onMutateResult, context) => {
      // insert the new entity as the first element of the default query (that is:
      // no special parameters, just sorted by creation date)
      for (const pageSize of availableTablePageSizes) {
        const listKey = options.queryKeyFactory.list({
          sort: {property: 'createdAt', order: 'DESC'},
          page: 1,
          pageSize: pageSize,
        })
        context.client.setQueryData(
          listKey,
          (previousPage: PaginatedResponse<TData>): PaginatedResponse<TData> => {
            // prepend new entity to list
            const updatedResults = [response, ...previousPage.results]
            // remove the last element if the page overflows the page size
            if(updatedResults.length > pageSize) updatedResults.pop()
            return {
              ...previousPage,
              results: updatedResults
            }
          }
        )
      }
      // create a detail cache entry for the new entity
      const detailKey = options.queryKeyFactory.detail(
        Number(getPKfromSelf(response.self))
      )
      context.client.setQueryData(detailKey, response)

      // propagate callback
      options.onSuccess && options.onSuccess(response, input, onMutateResult, context)
    },
    onSettled: async (
      response: TData | undefined,
      error: TError | null,
      input: TVariables,
      onMutateResult: TOnMutateResult | undefined,
      context: MutationFunctionContext
    ) => {
      // The mutation either was successful or failed. Invalidate all affected
      // queries to ensure consistency with the backend.
      // Note: there are no invalid detail queries after a create-mutation.
      await context.client.invalidateQueries({
        queryKey: options.queryKeyFactory.lists()
      })

      // propagate callback
      options.onSettled?.(response, error, input, onMutateResult, context)
    }
  }, queryClient)
}

export function useSmartDeleteMutation(
  options: UseSmartMutationOptions<
    void,
    DefaultError,
    string | number | (string | number)[], // an id or self string or array of such
    unknown
  >,
  queryClient?: QueryClient,
): UseMutationResult<void, DefaultError, (string | number) | (string | number)[]> {
  /* ----------------------- Utility functions start ------------------------ */
  /**
   * Takes an entity's self-link or primary key and returns the primary key as a string.
   * @param idOrSelf
   */
  function parseIdOrSelf(idOrSelf: string | number): string {
    return typeof idOrSelf === 'number'
      ? String(idOrSelf)
      : getPKfromSelf(idOrSelf)
  }
  /* ------------------------ Utility functions end ------------------------- */
  return useMutation({
    ...options,
    onSuccess: (
      response: void,
      toDelete: string | number | (string | number)[],
      onMutateResult: unknown,
      context: MutationFunctionContext
    ) => {
      // parse input variable
      if(Array.isArray(toDelete)) {
        if(toDelete.length === 0) return
        // toDelete is an array of idOrSelf arguments
        toDelete = toDelete.map(parseIdOrSelf)
      }
      // toDelete is a single idOrSelf argument
      else toDelete = [parseIdOrSelf(toDelete)]

      const activeKeys = getActiveQueryKeys(context.client)
      for (const key of activeKeys) {
        if (key.includes('list')) {
          // try to remove the deleted entity from list caches
          context.client.setQueryData(
            key,
            (previousPage: PaginatedResponse<EntityBaseType>): PaginatedResponse<EntityBaseType> => {
              const updatedResults = previousPage.results
                .filter(cached => !toDelete.includes(getPKfromSelf(cached.self)))
              return { ...previousPage, results: updatedResults }
            }
          )
        }
        if (key.includes('detail')) {
          // If the currently open detail matches any of the deleted entities, clear it
          const current = context.client.getQueryData(key) as DetailResponse<EntityBaseType>
          if (current && toDelete.includes(getPKfromSelf(current.self))) {
            context.client.setQueryData(key, undefined)
          }
        }
      }
      // propagate callback
      options.onSuccess && options.onSuccess(response, toDelete, onMutateResult, context)
    },
    onSettled: (
      response: void,
      error: Error | null,
      toDelete: string | number | (string | number)[],
      onMutateResult: unknown,
      context: MutationFunctionContext
    ) => {
      // The mutation either was successful or failed -> Invalidate all queries to
      // ensure consistency with the backend

      // parse input variable
      let pksToDelete: string[]
      if(Array.isArray(toDelete)) {
        if(toDelete.length === 0) return
        // toDelete is an array of idOrSelf arguments
        pksToDelete = toDelete.map(parseIdOrSelf)
      }
      // toDelete is a single idOrSelf argument
      else pksToDelete = [parseIdOrSelf(toDelete)]

      // invalidate queries
      // throw-away promise ...
      const _ = context.client.invalidateQueries({
        queryKey: options.queryKeyFactory.lists()
      })
      pksToDelete.forEach((pk: string) => {
        // throw-away promise
        const _ = context.client.invalidateQueries({
          queryKey: options.queryKeyFactory.details(Number(pk))
        })
      })

      // propagate callback
      options.onSettled?.(response, error, toDelete, onMutateResult, context)
    }
  }, queryClient)
}



/**
 * An optimistic update mutation hook that handles optimistic updates and cache
 * invalidation for you.
 * This hook is not meant to be used directly in React components but rather as
 * a utility to ease the implementation of specialized update mutation hooks.
 *
 * Why optimistic updates?:
 *  Whenever the user interacts with the UI and changes an entity, the UI should
 *  reflect the changes immediately to affirm the user that their actions actually
 *  did something. There are two ways to achieve this:
 *  We can either update the UI to display a loading state and reveal the updated
 *  data after we received the backend response (sometimes this even needs an
 *  additional refetch to also get expanded properties). Or we can optimistically
 *  update the UI to display the updated data immediately and rollback if we need to.
 *  Optimistic updates allow us to abstract away the fact that we need to do a backend
 *  request and wait for a response.
 *
 * Optimistic update strategy:
 *  1. Determine the currently active queries (that are queries that are currently
 *     being displayed to the user).
 *  2. Create snapshots of the cache entries for these queries and cancel outgoing
 *     queries to prevent our optimistic updates from being overwritten.
 *  3. Apply the optimistic update to the cache entries, and by doing so, update
 *     the UI to display the entity updates immediately.
 *  4. Invalidate all related, inactive queries (that are queries that might be
 *     affected by the entity being changed but are not currently being displayed),
 *     such that they are refetched when the user 'navigates to them'.
 *
 * @param options
 */
// export function useOptimisticUpdateMutation<
//   TData extends EntityBaseType,
//   TError = DefaultError,
//   TVariables = void,
//   TOnMutateResult = unknown,
// >(
//   options: UseSmartMutationOptions<TData, TError, TVariables, TOnMutateResult>
// ) {
//   /* ------------------------- Non state data start ------------------------- */
//   const log = new LogTool({context: 'useSmartUpdateMutation', logLevel: 'debug', enable: true})
//   /* -------------------------- Non state data end -------------------------- */
//   return useMutation({
//     mutationFn: options.mutationFn,
//     onMutate: async (
//       updatedEntity: TData,
//       context: MutationFunctionContext
//     ): Promise<{
//       cacheSnapshot: CacheSnapshot<TData>,
//       callbackReturnValue: unknown
//     }> => {
//       log.info(
//         'Begin mutation. Try to apply optimistic updates for: ',
//         updatedEntity.self
//       )
//       // 1. Retrieve the keys for all active queries. These are (in the sense of
//       //    the optimistic update) the most relevant queries, as they are currently
//       //    being displayed.
//       const activeKeys = getActiveQueryKeys(context.client)
//       log.debug('Found active queries ->', activeKeys)
//
//       // 2. Create snapshots and cancel outgoing queries that would otherwise
//       //    overwrite our optimistic update.
//       const cacheSnapshot: CacheSnapshot<TData> = {
//         listSnapshots: [],
//         detailSnapshots: []
//       }
//       for(const key of activeKeys) {
//         // a) cancel any outgoing queries
//         await context.client.cancelQueries({queryKey: key,})
//
//         // b) create a snapshot of the current cache entry and ...
//         // 3. Apply the optimistic update to the cache entry
//         if(key.includes('list')) {
//           // ... create snapshot
//           cacheSnapshot.listSnapshots.push({
//             queryKey: key,
//             rollbackValue: context.client.getQueryData(key) as PaginatedResponse<TData>
//           })
//           // ... apply update
//           context.client.setQueryData(
//             key,
//             (previousPage: PaginatedResponse<TData>): PaginatedResponse<TData> => {
//               const updatedResults = [...previousPage.results]
//               if(Array.isArray(updatedResults) && updatedResults.length > 0) {
//                 const index = updatedResults.findIndex(
//                   cachedEntity => cachedEntity.self === updatedEntity.self
//                 )
//                 if(index !== -1) updatedResults[index] = updatedEntity
//               }
//               return {...previousPage, results: updatedResults}
//             }
//           )
//         }
//         if(key.includes('detail')) {
//           // ...create snapshot
//           cacheSnapshot.detailSnapshots.push({
//             queryKey: key,
//             rollbackValue: context.client.getQueryData(key) as DetailResponse<TData>
//           })
//           // ... apply update
//           context.client.setQueryData(key, updatedEntity)
//         }
//       }
//       // 4. propagate callback
//       const callbackReturnValue: unknown = options.onMutate && options.onMutate(updatedEntity, context)
//
//       // 5. Return the previously cached data to enable rollbacks if the mutation fails
//       log.debug('Create snapshot ->', cacheSnapshot)
//       return {
//         cacheSnapshot,
//         callbackReturnValue
//       }
//     },
//     onError: (error, updatedEntity, onMutateResult, context) => {
//       // the mutation failed -> rollback the optimistic update
//       log.error(
//         'Error carrying out mutation. Rollback cache to before optimistic update.'
//       )
//       if(onMutateResult) {
//         for(const listSnapshot of onMutateResult.cacheSnapshot.listSnapshots) {
//           context.client.setQueryData(
//             listSnapshot.queryKey,
//             listSnapshot.rollbackValue,
//           )
//         }
//         for(const detailSnapshot of onMutateResult.cacheSnapshot.detailSnapshots) {
//           context.client.setQueryData(
//             detailSnapshot.queryKey,
//             detailSnapshot.rollbackValue,
//           )
//         }
//       }
//       // propagate callback
//       options.onError && options.onError(
//         error,
//         updatedEntity,
//         onMutateResult?.callbackReturnValue, // only propagate the return value of the onMutate callback
//         context
//       )
//     },
//     onSuccess: async (_, __, onMutateResult, context) => {
//       // the mutation was successful -> invalidate all affected queries that we
//       // did not manually update
//       log.info('Success carrying out mutation')
//       await context.client.invalidateQueries({type: 'inactive'})
//
//       // propagate callback
//       options.onSuccess && options.onSuccess(
//         _,
//         __,
//         onMutateResult?.callbackReturnValue, // only propagate the return value of the onMutate callback
//         context
//       )
//     }
//   })
// }